import axios from "axios";
import { format } from "date-fns";
import { makeAutoObservable, runInAction } from "mobx";

interface TableJson {
  table_header: string[];
  table_content: { [key: string]: string | number }[];
}

export enum Step {
  CLEAR = 'clear',
  CREATE_ROUTE = 'createRoute',
  LIST_PRACTITIONERS = 'listPractitioners'
}

interface OllamaApiResponse {
  model: string;
  created_at: string;
  response: string;
  text: string;
  vid: string;
  pic: string;
  waiting: boolean;
  done: boolean;
  questions: string[];
  table_json: TableJson;
  render_table: boolean;
}

interface llmResponse {
  success: boolean;
  error_msg: string;
  payload: OllamaApiResponse;
}

interface ChatEntry {
  message: string;
  tableJson?: TableJson;
  messageId?: string;
  thoughtSteps?: any[];
}

interface UserData {
  userName: string;
  userFriendlyName: string;
  userPicture: string;
  impersonateList: string[];
  isAdmin: boolean;
  earlyRelease: boolean;
}

interface Route {
  waypoints: any[];
  distance: number;
  duration: number;
  geometry: any;
}

interface CheckIn {
  notes?: string;
  followUp?: string;
  practitioner_id?: number;
  practitioner_name?: string;
}

interface PractitionerGoals {
  ytd_sales: number;
  goal: number;
  goal_percentage: number;
}

interface AvailableProduct {
  id: number;
  unit_price: number;
  name: string;
  sku: string;
}

interface Conversation {
  id: number;
  title: string;
  lastUpdated: Date;
  messageCount: number;
}

class Store {
  userInput = "";
  prompts: string[] = [""];
  promptsFeedback: string[] = [""];
  answers: ChatEntry[] = [];
  textDisplay = "";
  picURL = "";
  vidURL = "";
  renderCustom = false;
  heatmap: any = [];
  currProgress: number = 0.0;
  yearGoal: number = 0.0;
  refreshDataTime: string = '';
  currentRoute: Route | null = null;
  currentPractitioner: any | null = {};
  selectedPractitioners: any[] = [];
  practitionersFilteredByFMC: any[] = [];
  backButtonHistory: string[] = [];
  currentDirections: number[][] = [];
  initialLatLon: [number, number] | null = null;
  practitionersCarouselFilter: string = "Geo-Verified";
  practitionersFilterMultiSelectValue: string[] = ["Name", "Address", "City", "State", "Specialty", "Email", "Zipcode", "Tags"];
  currentActiveRoute: any = null;
  temporaryNotes: Map<string, any> = new Map();

  // Search by follow-up date
  selectedFollowUpDate: string | null = format(new Date(), 'yyyy-MM-dd');
  practitionersInFollowUpDate: any[] = [];
  currentPractitionerNextFollowUpDate: string | null = null;
  currentPractitionerLastCheckinDate: string | null = null;

  myQuestions: string[] = [];  // Added to handle multiple questions
  checkIn: CheckIn | null = {}

  currentQuestionIndex: number = 0;
  waitingForAnswers: boolean = false;

  user: UserData | null = null;
  impersonatedUser: string | null = null;  // This is an email

  // View-As info - matched by impersonatedUser
  impersonatedUserName: string | null = null;
  impersonatedUserNetsuiteID: number | null = null;
  impersonatedUserRsdName: string | null = null;
  impersonatedUserRsdEmail: string | null = null;
  impersonatedUserAlias: string | null = null; // This is an alias email


  routingModalStep: Step = Step.LIST_PRACTITIONERS;
  practitionerGoals: PractitionerGoals | null = null;
  // generalNotes: string | null = null;
  availableProducts: AvailableProduct[] = [];

  // Cache for practitioners and leads data (keyed by user email, then page)
  practitionersCache: Record<string, Record<number, any[]>> = {};
  leadsCache: Record<string, Record<number, any[]>> = {};

  // Cache for search term
  cachedSearchTerm: string = '';

  // Cache for total count
  cachedTotalCount: number = 0;

  // Chat conversation management
  conversations: Conversation[] = [];
  activeConversationId: number | null = null;
  isLoadingConversations = false;

  constructor() {
    makeAutoObservable(this);
    // Note: Conversation restoration from localStorage happens AFTER Store initialization
    // via restoreConversationFromStorage() method to ensure proper message loading
  }

  setCurrentActiveRoute(a: any) {
    this.currentActiveRoute = a;
  }

  setInitialLatLon(ll?: [number, number]) {
    if (!ll || ll.length < 2) {
      this.initialLatLon = null;
    } else {
      const [lat, lon] = ll;
      if (!lat || !lon) {
        this.initialLatLon = null;
      } else {
        this.initialLatLon = ll;
      }
    }
  }

  setCurrentDirections(ds: number[][]) {
    this.currentDirections = ds;
  }

  setAvailableProducts(av: AvailableProduct[]) {
    this.availableProducts = av;
  }

  // Cache management methods
  setPractitionersCache(userEmail: string, page: number, data: any[]) {
    if (!this.practitionersCache[userEmail]) {
      this.practitionersCache[userEmail] = {};
    }
    this.practitionersCache[userEmail][page] = data;
  }

  getPractitionersCache(userEmail: string, page: number): any[] | undefined {
    return this.practitionersCache[userEmail]?.[page];
  }

  setLeadsCache(userEmail: string, page: number, data: any[]) {
    if (!this.leadsCache[userEmail]) {
      this.leadsCache[userEmail] = {};
    }
    this.leadsCache[userEmail][page] = data;
  }

  getLeadsCache(userEmail: string, page: number): any[] | undefined {
    return this.leadsCache[userEmail]?.[page];
  }

  // Total count cache methods
  setCachedTotalCount(count: number) {
    this.cachedTotalCount = count;
  }

  getCachedTotalCount(): number {
    return this.cachedTotalCount;
  }

  clearPractitionersCache(userEmail?: string) {
    if (userEmail) {
      delete this.practitionersCache[userEmail];
    } else {
      this.practitionersCache = {};
    }
  }

  clearLeadsCache(userEmail?: string) {
    if (userEmail) {
      delete this.leadsCache[userEmail];
    } else {
      this.leadsCache = {};
    }
  }

  clearAllCache(userEmail?: string) {
    if (userEmail) {
      delete this.practitionersCache[userEmail];
      delete this.leadsCache[userEmail];
    } else {
      this.practitionersCache = {};
      this.leadsCache = {};
    }
    this.cachedTotalCount = 0;
  }

  // Search term cache methods
  setCachedSearchTerm(searchTerm: string) {
    this.cachedSearchTerm = searchTerm;
  }

  getCachedSearchTerm(): string {
    return this.cachedSearchTerm;
  }

  clearCachedSearchTerm() {
    this.cachedSearchTerm = '';
  }

  setPractitionersFilterMultiSelectValue(multiSelectValue: string[]) {
    this.practitionersFilterMultiSelectValue = multiSelectValue;
  }

  getPractitionersFilterMultiSelectValue(): string[] {
    return this.practitionersFilterMultiSelectValue;
  }

  setPractitionersCarouselFilter(filter: string) {
    this.practitionersCarouselFilter = filter;
  }

  getPractitionersCarouselFilter(): string {
    return this.practitionersCarouselFilter;
  }

  setPractitionersFilteredByFMC(practitioners: any[]) {
    this.practitionersFilteredByFMC = practitioners
      .map((p) => {
        const defaultAddress = p.addresses.find((a: any) => a.id === p.default_address);
        return {
          ...defaultAddress,
          ...p,
        }
      });
  }

  setCheckIn(newCheckIn: CheckIn) {
    this.checkIn = { ...this.checkIn, ...newCheckIn }
  }

  clearCheckIn() {
    this.checkIn = null
  }

  setGeneralNotes(notes: string) {
    this.currentPractitioner.general_notes = notes;
  }

  setRoutingModalStep(step: Step) {
    this.routingModalStep = step;
  }

  addToRoute(practitioner: any) {
    if (this.selectedPractitioners.filter((pract) => pract.id === practitioner.id).length === 0) {
      // Preserve first and last location flags
      const firstLocation = this.selectedPractitioners[0]?.isFirstLocation ? this.selectedPractitioners[0] : null;
      const lastLocation = this.selectedPractitioners[this.selectedPractitioners.length - 1]?.isLastLocation ? this.selectedPractitioners[this.selectedPractitioners.length - 1] : null;
      const middlePractitioners = this.selectedPractitioners.filter(p => !p.isFirstLocation && !p.isLastLocation)

      // Add new practitioner to middle practitioners
      middlePractitioners.push(practitioner);

      // Reconstruct the array with preserved first and last locations
      this.selectedPractitioners = [
        firstLocation,
        ...middlePractitioners
          .map((p) => {
            const defaultAddress = p.addresses.find((a: any) => a.id === p.default_address);
            return {
              ...defaultAddress,
              ...p,
            }
          }),
        lastLocation
      ].filter(item => item !== null);
    }
  }

  removeFromRoute(practitioner: any) {
    this.selectedPractitioners = this.selectedPractitioners.filter((pract) => pract.id !== practitioner.id);
    console.log('selectedPractitioners', this.selectedPractitioners.length)
  }

  setSelectedPractitioners(practitioners: any[]) {
    // bringing default address to route, to prevent bugs on route
    this.selectedPractitioners = practitioners.map((p) => {
      const defaultAddress = p?.addresses?.find((a: any) => a.id === p.default_address);
      if (defaultAddress) {
        return {
          ...defaultAddress,
          ...p,
        }
      }
      return p;
    });
  }

  setCurrentRoute(route: Route) {
    this.currentRoute = route;
  }

  setRefreshDataTime(time: string) {
    this.refreshDataTime = time;
  }

  setCurrentPractitioner(practitioner: any) {
    this.currentPractitioner = {
      ...practitioner,
    };
  }

  setCurrProgress(val: number) {
    this.currProgress = val;
  }

  setYearGoal(val: number) {
    this.yearGoal = val;
  }

  setPractitionerGoals(goals: PractitionerGoals) {
    this.practitionerGoals = goals
  }

  setUser(user: UserData) {
    this.user = user;
    // Conversation restoration from localStorage handled by restoreConversationFromStorage()
  }

  setImpersonatedUser(user: string) {
    this.impersonatedUser = user;
    this.setUserInput('');
    this.prompts = [""];
    this.answers = []; // Reset answers to show landing page greeting
    this.activeConversationId = null; // Clear conversation when switching users
  }

  setImpersonatedUserName(val: string) {
    this.impersonatedUserName = val;
  }

  setImpersonatedNetsuiteID(val: number) {
    this.impersonatedUserNetsuiteID = val;
  }

  setImpersonatedRsdName(val: string) {
    this.impersonatedUserRsdName = val;
  }

  setImpersonatedRsdEmail(val: string) {
    this.impersonatedUserRsdEmail = val;
  }

  setImpersonatedUserAlias(val: string) {
    this.impersonatedUserAlias = val;
  }

  setUserInput(value: string) {
    this.userInput = value;
  }

  addPrompt(prompt: string) {
    this.prompts.push(prompt);
  }

  addPromptFeedback(feedback: string, position: number) {
    this.promptsFeedback[position] = feedback;
  }

  setAnswers(newAnswer: string) {
    this.answers = [{ message: newAnswer }];
  }

  addAnswer(answer: string | ChatEntry, is_interrupted: boolean = false) {
    console.log('is_interrupted:', is_interrupted)
    if (is_interrupted) {
      this.answers.push({ message: "*Sales assistant AI was stopped by user.*" });
      return;
    }
    console.log('add answer:', answer)
    if (typeof answer === 'string') {
      this.answers.push({ message: answer });
    } else {
      this.answers.push(answer);
    }
  }

  updateAnswer(index: number, newMessage: string) {
    if (this.answers[index]) {
      // Create new object to ensure MobX detects the change
      this.answers[index] = {
        ...this.answers[index],
        message: newMessage
      };
    }
  }

  updateThoughtProcess(index: number, steps: any[]) {
    if (this.answers[index]) {
      this.answers[index].thoughtSteps = steps;
    }
  }

  setText(text: string) {
    this.textDisplay = text;
  }

  setPic(pic: string) {
    this.picURL = pic;
  }

  setVid(vid: string) {
    this.vidURL = vid;
  }

  setRenderCustom(val: boolean) {
    this.renderCustom = val;
  }

  setHeatmap(val: any) {
    this.heatmap = val;
  }

  setMyQuestions(questions: string[]) {
    this.myQuestions = questions;
  }

  setCurrentQuestionIndex(value: number) {
    this.currentQuestionIndex = value;
  }

  setWaitingForAnswers(value: boolean) {
    this.waitingForAnswers = value;
  }

  getTimeGreeting(): string {
    const now = new Date();
    const hour = now.getHours();

    if (hour < 12) {
      return "morning";
    } else if (hour < 18) {
      return "afternoon";
    } else {
      return "evening";
    }
  }

  async sendMsgToLLMService(msg: string, user_name: string, resume: boolean, answers_array: string[]): Promise<OllamaApiResponse> {

    try {
      const response = await axios.post<llmResponse>('http://127.0.0.1:8503/api/call_dummy',
        {
          'message': msg,
          'user_name': 'admin',
          'resume': resume,
          'answers_array': answers_array
        }
      );

      if (response.status >= 200 && response.status < 300 && response.data.success === true) {
        const result: OllamaApiResponse = response.data.payload;

        return result;
      } else {
        console.error('Server error:', response.status, response.statusText);
        return {
          model: '',
          created_at: '',
          response: "There is an issue pulling your data.  We will work with DFH IT to fix this.  In the meantime, can you try another prompt?",
          text: '',
          vid: 'nan',
          pic: 'nan',
          waiting: false,
          done: false,
          questions: [],
          table_json: {
            table_header: [],
            table_content: [{}],
          },
          render_table: false,
        };
      }

    } catch (error: any) {
      console.error('Network error:', error);
      return {
        model: '',
        created_at: '',
        response: "I'm not connected to the internet -- I can't answer your question at this point...",
        text: '',
        vid: '',
        pic: '',
        waiting: false,
        done: false,
        questions: [],
        table_json: {
          table_header: [],
          table_content: [{}],
        },
        render_table: false,
      };
    }
  }

  addToBackButtonHistory(path: string) {
    this.backButtonHistory.push(path);
  }
  popBackButtonHistory() {
    this.backButtonHistory.pop();
  }


  setTemporaryNotes(key: string, value: any) {
    this.temporaryNotes.set(key, value);
  }

  getTemporaryNotes(key: string): any {
    return this.temporaryNotes.get(key);
  }

  clearTemporaryNotes(key: string) {
    this.temporaryNotes.delete(key);
  }

  clearAllTemporaryNotes() {
    this.temporaryNotes.clear();
  }

  setFollowUpDateSearch(date: string | null) {
    this.selectedFollowUpDate = date;
  }

  setPractitionersInFollowUpDate(practitioners: any[]) {
    this.practitionersInFollowUpDate = practitioners;
  }

  setCurrentPractitionerLastCheckinDate(date: string | null) {
    this.currentPractitionerLastCheckinDate = date;
  }

  setCurrentPractitionerNextFollowUpDate(date: string | null) {
    this.currentPractitionerNextFollowUpDate = date;
  }

  // Conversation management methods
  setConversations(conversations: Conversation[]) {
    this.conversations = conversations;
  }

  setActiveConversationId(id: number | null) {
    runInAction(() => {
      this.activeConversationId = id;
    });
  }

  async loadConversations(postData: (endpoint: string, data: any) => Promise<any>) {
    runInAction(() => {
      this.isLoadingConversations = true;
    });

    try {
      const response = await postData('/api/list_conversations', {
        fmc_email: this.impersonatedUser || this.user?.userName
      });

      if (response && response.success) {
        runInAction(() => {
          this.conversations = response.conversations.map((conv: any) => ({
            id: conv.id,
            title: conv.title,
            lastUpdated: new Date(conv.last_message_at),
            messageCount: conv.message_count
          }));
        });
      }
    } catch (error) {
      // Silently handle error
    } finally {
      runInAction(() => {
        this.isLoadingConversations = false;
      });
    }
  }

  createNewConversation() {
    // Clear the active conversation and chat state for a fresh start
    this.activeConversationId = null;
    this.prompts = [""];
    this.answers = [];
    this.promptsFeedback = [""];
    this.userInput = "";
  }

  async loadConversation(id: number, apiUrl: string, postData: (endpoint: string, data: any) => Promise<any>) {
    try {
      // Call backend API to get conversation messages
      const response = await postData('/api/load_conversation', {
        conversation_id: id
      });

      if (response && response.success && response.messages) {
        // Wrap all state mutations in runInAction for MobX strict mode
        runInAction(() => {
          // Set as active conversation
          this.activeConversationId = id;

          // Clear current chat state
          this.prompts = [""];
          this.answers = [];
          this.promptsFeedback = [""];
          this.userInput = "";

          // Rebuild prompts and answers from messages
          // Messages should be ordered chronologically: user question, then assistant response
          // prompts[0] is always empty (for welcome screen)
          // prompts[i] pairs with answers[i-1]
          response.messages.forEach((msg: any) => {
            if (msg.role === 'user') {
              this.prompts.push(msg.content);
              this.promptsFeedback.push("");
            } else if (msg.role === 'assistant') {
              this.answers.push({
                message: msg.content,
                messageId: msg.timestamp || '',
                thoughtSteps: []
              });
            }
          });
        });
      }
    } catch (error) {
      // Silently handle error
    }
  }

  archiveConversation(id: number) {
    // Remove from local state immediately (optimistic update)
    this.conversations = this.conversations.filter(conv => conv.id !== id);

    // If archived conversation was active, clear it
    if (this.activeConversationId === id) {
      this.activeConversationId = null;
    }
  }
}

export const store = new Store();